import { OAUTH2_CLIENT_ID, OAUTH2_ISSUER_URI, OAUTH2_SCOPES } from '@constants';
import Keycloak from 'keycloak-js';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface AuthContextType {
  initialized: boolean;
  authenticated: boolean;
  token?: string;
  tokenParsed?: Keycloak.KeycloakTokenParsed;
  // eslint-disable-next-line no-unused-vars
  login: (options?: Keycloak.KeycloakLoginOptions) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  logout: (options?: Keycloak.KeycloakLogoutOptions) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  initialized: false,
  authenticated: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export default function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const keycloak = useMemo(
    () =>
      new Keycloak({
        url: OAUTH2_ISSUER_URI.replace(/\/realms\/.*$/, '/'),
        realm: OAUTH2_ISSUER_URI.split('/').pop() || '',
        clientId: OAUTH2_CLIENT_ID,
      }),
    [],
  );

  const hasKeycloakInit = useRef(false);
  const [initialized, setInitialized] = useState(false);
  const [authState, setAuthState] = useState<{
    authenticated: boolean;
    token?: string;
    tokenParsed?: Keycloak.KeycloakTokenParsed;
  }>({ authenticated: false });

  const syncAuthState = useCallback(() => {
    setAuthState({
      authenticated: !!keycloak.authenticated,
      token: keycloak.token,
      tokenParsed: keycloak.tokenParsed,
    });
  }, [keycloak]);

  useEffect(() => {
    if (hasKeycloakInit.current) return;
    hasKeycloakInit.current = true;

    keycloak
      .init({
        scope: OAUTH2_SCOPES,
        onLoad: 'check-sso',
        silentCheckSsoFallback: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        // The IDP session status iframe runs cross-site (register.*.probonio.de ->
        // auth.*.bikeleasing.de) and is blocked by third party cookie policies. It then
        // reports "changed" and keycloak-js clears the token, which redirects the app to
        // the login every 5 seconds. keycloak-js also polls that iframe inside
        // updateToken, so leaving it enabled breaks the token refresh below as well.
        // Session invalidation is still detected by the refresh failing.
        checkLoginIframe: false,
      })
      .then(() => {
        syncAuthState();
        setInitialized(true);
      })
      .catch(() => setInitialized(true))
      .finally(
        () =>
          import.meta.env.DEV &&
          // eslint-disable-next-line no-console
          console.debug('KeycloakJS initialized', {
            authenticated: keycloak.authenticated,
            tokenParsed: keycloak.tokenParsed,
          }),
      );
  }, [keycloak, syncAuthState]);

  useEffect(() => {
    if (!keycloak.authenticated) return;

    const interval = setInterval(async () => {
      if (!authState.authenticated || !keycloak.refreshToken) return;

      await keycloak.updateToken(30).catch(() => undefined);
      syncAuthState();
    }, 60_000);

    return () => clearInterval(interval);
  }, [authState.authenticated, keycloak, syncAuthState]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- Keycloak requires setting handlers via mutation
    keycloak.onAuthSuccess = syncAuthState;
    keycloak.onAuthRefreshSuccess = syncAuthState;
    keycloak.onAuthLogout = syncAuthState;
    keycloak.onTokenExpired = () => keycloak.updateToken(0).catch(() => syncAuthState());

    return () => {
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
    };
  }, [keycloak, syncAuthState]);

  const login = useCallback(
    async (options?: Keycloak.KeycloakLoginOptions) =>
      await keycloak.login({
        redirectUri: `${window.location.origin}/oauth`,
        ...options,
      }),
    [keycloak],
  );

  const logout = useCallback(
    async (options?: Keycloak.KeycloakLogoutOptions) =>
      await keycloak.logout({
        redirectUri: window.location.origin,
        ...options,
      }),
    [keycloak],
  );

  const contextValue = useMemo(
    () => ({
      initialized,
      authenticated: authState.authenticated,
      token: authState.token,
      tokenParsed: authState.tokenParsed,
      login,
      logout,
    }),
    // Depend on the individual fields, not on authState: syncAuthState always stores a
    // fresh object, so depending on it would rebuild the value on every sync.
    [initialized, authState.authenticated, authState.token, authState.tokenParsed, login, logout],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
